import React, { useState, useEffect } from "react";
import axios from "axios";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ArrowLeftIcon, ArrowRightIcon, Download } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { FaFileInvoice } from "react-icons/fa";

import "../assets/css/GroupLoanCollectionReport.css";
import { fetchUserProfile } from "../redux/slices/branch/userProfileSlice";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const GroupLoanCollectionReport = () => {
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.userProfile);
  const navigate = useNavigate();

  /* ---------- STATE ---------- */
  const [centers, setCenters] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedCenter, setSelectedCenter] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [loanTypeFilter, setLoanTypeFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [collectionData, setCollectionData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [company, setCompany] = useState("");

  const ITEMS_PER_PAGE_OPTIONS = [10, 20, 50, 100, "All"];

  const formatDateDDMMYY = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = String(d.getFullYear());
    return `${dd}/${mm}/${yy}`;
  };

  /* ---------- PROFILE ---------- */
  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  /* ---------- FETCH CENTERS ---------- */
  useEffect(() => {
    if (!profile?.id) return;

    const fetchCenters = async () => {
      try {
        const res = await axios.get(
          `https://payyug.in/api/group/centre?LoanofficerId=${profile.id}&limit=10000`,
        );
        setCenters(res.data?.data || []);
      } catch (err) {
        console.error("Center fetch error:", err);
      }
    };

    fetchCenters();
  }, [profile?.id]);

  /* ---------- FETCH GROUPS ---------- */
  useEffect(() => {
    if (!selectedCenter) return;

    const fetchGroups = async () => {
      try {
        const res = await axios.get(
          `https://payyug.in/api/groupupdate/getall?LoanofficerId=${profile?.id}&CenterId=${selectedCenter}&limit=10000`,
        );
        setGroups(res.data?.data || []);
      } catch (err) {
        console.error("Group fetch error:", err);
      }
    };

    fetchGroups();
  }, [selectedCenter, profile?.id]);

  /* ---------- FETCH COLLECTION ---------- */
  const fetchCollection = async (page = 1) => {
    if (!profile?.superAdminId) return;

    try {
      setLoading(true);

      const res = await axios.get(
        "https://payyug.in/api/group-loan/emi/collection-report",
        {
          params: {
            superAdminId: profile.superAdminId,
            // branchId: profile.branchId,
            centerId: selectedCenter || undefined,
            groupId: selectedGroup || undefined,
            page,
            loanType: loanTypeFilter || undefined,
            fromDate: fromDate || undefined,
            toDate: toDate || undefined,
            limit: itemsPerPage === "All" ? 100000 : itemsPerPage,
          },
        },
      );

      console.log("collection report", res.data);

      if (res.data?.success) {
        setCollectionData(res.data.data || []);
        setCurrentPage(res.data.pagination.currentPage);
        setTotalPages(res.data.pagination.totalPages);
        setTotalRecords(res.data.pagination.totalRecords);
      }
    } catch (err) {
      console.error("Collection fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  /* ---------- PAGINATION / DEPENDENCIES ---------- */
  useEffect(() => {
    if (profile?.superAdminId) {
      fetchCollection(currentPage);
    }
  }, [currentPage, itemsPerPage, profile]);

  /* ---------- SEARCH FILTER ---------- */
  const filteredData = collectionData.filter((r) => {
    const text = `
      ${r.loanAccount?.loanAccountNumber}
      ${r.loanAccount?.memberName}
      ${r.loanAccount?.centerName}
      ${r.loanAccount?.groupName}
    `.toLowerCase();

    return text.includes(searchQuery.toLowerCase());
  });

  /* ---------- FILTER SUBMIT ---------- */
  const handleFilter = () => {
    setCurrentPage(1);
    fetchCollection(1);
  };

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const res = await fetch(
          `https://payyug.in/api/get-company?superAdminId=${profile?.superAdminId}`,
        );
        const data = await res.json();
        if (res.ok && data) {
          if (data.logo)
            data.logoUrl = `https://payyug.in/${data.logo.replace(/^images\//, "")}`;
          setCompany(data);
        }
      } catch (err) {
        console.error("Failed to fetch company details", err);
      }
    };

    if (!profile?.superAdminId) return;
    fetchCompany();
  }, [profile?.superAdminId]);

  // 🔹 Export to Excel
  const exportExcel = () => {
    const data = filteredData.map((item) => ({
      State: item.member?.state || "N/A",
      Region: profile?.branchName||item.member?.district || "N/A",
      Branch: profile?.branchName || "N/A",
      "Loan Officer Name": item.loanAccount?.officerName || "N/A",
      "Account No": `Group Loan ${item.loanAccount?.loanType ? `(${item.loanAccount?.loanType})`:""}`,
      "Loan Type": item.loanAccount?.loanType&&`Group Loan (${item.loanAccount.loanType})`,
      "Loan Amount": item.loanAccount?.loanAmount
        ? item.loanAccount?.loanAmount
        : 0,
      "Center Name": item.loanAccount?.centerName || "N/A",
      "Group Name": item.loanAccount?.groupName || "N/A",
      "Member Name": item.loanAccount?.memberName || "N/A",
      "Guardian Name": item.member?.guardianName || "N/A",
      "Mobile No.": item.member?.mobileNumber || "N/A",
      "Emi No.": item.installmentNo || "N/A",
      "Emi Amount": item.emiAmount ? item.emiAmount : 0,
      "Paid Amount": item.paidAmount ? item.paidAmount : 0,
      "Outstanding amount": item.outstanding ? item.outstanding : 0,
      "Paid Date": item.paidDate ? formatDateDDMMYY(item.paidDate) : "N/A",
      Status: item.status || "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);

    // 🔹 Header styling
    const headerCells = Object.keys(data[0]);
    headerCells.forEach((_, index) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: index });
      worksheet[cellRef].s = {
        font: {
          bold: true,
          color: { rgb: "FFFFFF" },
        },
        fill: {
          fgColor: { rgb: "1F4E78" }, // dark blue
        },
        alignment: {
          horizontal: "center",
          vertical: "center",
        },
      };
    });

    // 🔹 Auto column width
    worksheet["!cols"] = headerCells.map((key) => ({
      wch: Math.max(key.length + 5, 18),
    }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Loan Report");

    XLSX.writeFile(workbook, "Group_Collection_Report.xlsx");
  };

  // 🔹 Export to CSV
  const exportCSV = () => {
    const data = filteredData.map((item) => ({
      "State": item.member?.state || "N/A",
      Region: profile?.branchName||item.member?.district || "N/A",
      Branch: profile?.branchName || "N/A",
      "Loan Officer Name": item.loanAccount?.officerName || "N/A",
      "Account No": item.loanAccount?.loanAccountNumber || "N/A",
"Loan Type": `Group Loan ${item.loanAccount?.loanType ? `(${item.loanAccount?.loanType})`:""}`,
      "Loan Amount": item.loanAccount?.loanAmount
        ? item.loanAccount?.loanAmount
        : 0,
      "Center Name": item.loanAccount?.centerName || "N/A",
      "Group Name": item.loanAccount?.groupName || "N/A",
      "Member Name": item.loanAccount?.memberName || "N/A",
      "Guardian Name": item.member?.guardianName || "N/A",
      "Mobile No.": item.member?.mobileNumber || "N/A",
      "Emi No.": item?.installmentNo || "N/A",
      "Emi Amount": item.emiAmount ? item.emiAmount : 0,
      "Paid Amount": item.paidAmount ? item.paidAmount : 0,
      "Outstanding amount": item.outstanding ? item.outstanding : 0,
      "Paid Date": item.paidDate ? formatDateDDMMYY(item.paidDate) : "N/A",
      "Status": item.status || "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const csvOutput = XLSX.utils.sheet_to_csv(worksheet);

    const blob = new Blob([csvOutput], {
      type: "text/csv;charset=utf-8;",
    });

    saveAs(blob, "Group_Collection_Report.csv");
  };

  // 🔹 Export to PDF
  const exportPDF = async () => {
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    // ================= LOGO =================
    const logoX = 15;
    const logoY = 12;
    const logoSize = 25;
    const logoPadding = 2;

    // Border color & thickness
    doc.setDrawColor(0);
    doc.setLineWidth(0.2);

    // Calculate center of logo
    const centerX = logoX + logoSize / 2;
    const centerY = logoY + logoSize / 2;

    // Radius = half of logo + padding
    const radius = logoSize / 2 + logoPadding;

    // Draw circle
    doc.circle(centerX, centerY, radius);

    // Logo image
    if (company?.logoUrl) {
      try {
        const logoResp = await fetch(company.logoUrl);
        const logoBlob = await logoResp.blob();
        const reader = new FileReader();
        await new Promise((resolve) => {
          reader.onload = () => {
            doc.addImage(
              reader.result,
              "PNG",
              logoX,
              logoY,
              logoSize,
              logoSize,
            );
            resolve();
          };
          reader.readAsDataURL(logoBlob);
        });
      } catch {
        // silent fail
      }
    }

    // ================= COMPANY NAME (AUTO WRAP) =================
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);

    const nameX = logoX + logoSize + 5;
    const nameY = 18;
    const nameMaxWidth = pageWidth - nameX - 15;

    const companyNameText = doc.splitTextToSize(
      (company?.name || "COMPANY NAME").toUpperCase(),
      nameMaxWidth,
    );

    doc.text(companyNameText, nameX, nameY);

    // Calculate Y after company name
    let currentY = nameY + companyNameText.length * 6;

    // ================= COMPANY DETAILS =================
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);

    const detailX = nameX;
    const detailMaxWidth = nameMaxWidth;

    // Address
    const addressText = doc.splitTextToSize(
      company?.address || "Company Address",
      detailMaxWidth,
    );
    doc.text(addressText, detailX, currentY);
    currentY += addressText.length * 5;

    // Email
    const emailText = doc.splitTextToSize(
      `Email: ${company?.email || "-"}`,
      detailMaxWidth,
    );
    doc.text(emailText, detailX, currentY);
    currentY += emailText.length * 5;

    // Phone
    const phoneText = doc.splitTextToSize(
      `Phone: ${company?.contact || "-"}`,
      detailMaxWidth,
    );
    doc.text(phoneText, detailX, currentY);
    currentY += phoneText.length * 5;

    // Divider
    doc.line(10, currentY + 4, pageWidth - 10, currentY + 4);

    // ================= REPORT TITLE =================
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("Group Collection Report", pageWidth / 2, currentY + 14, {
      align: "center",
    });

    const tableColumn = [
      "SN",
      "Account No",
      "Loan Type",
      "Loan Amount",
      "Center",
      "Group",
      "Member",
      "Mobile",
      "Emi No.",
      "Emi Amount",
      "Paid Amount",
      "Paid Date",
      "Status",
    ];
    const tableRows = [];

    filteredData.forEach((item, index) => {
      const loanData = [
        index + 1,
        item.loanAccount?.loanAccountNumber || "N/A",
       `Group Loan ${item.loanAccount?.loanType ? `(${item.loanAccount?.loanType})`:""}`,
        item.loanAccount?.loanAmount ? item.loanAccount?.loanAmount : 0,
        item.loanAccount?.centerName || "N/A",
        item.loanAccount?.groupName || "N/A",
        item.loanAccount?.memberName || "N/A",
        item.member?.mobileNumber || "N/A",
        item?.installmentNo || "N/A",
        item.emiAmount ? item.emiAmount : 0,
        item.paidAmount ? item.paidAmount : 0,
        item.paidDate ? formatDateDDMMYY(item.paidDate) : "N/A",
        item.status || "N/A",
      ];
      tableRows.push(loanData);
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: currentY + 20,
      theme: "grid",
      styles: {
        fontSize: 7,
        cellPadding: 3,
        halign: "center",
      },
      headStyles: {
        fontStyle: "bold",
        fontSize: 7,
      },
      footStyles: {
        fillColor: [220, 220, 220],
        textColor: 20,
        fontStyle: "bold",
        fontSize: 8,
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
    });
    doc.save("Group_Collection_Report.pdf");
  };

  /* ---------- UI ---------- */
  return (
    <div className="glcr-container">
      {/* Back wrapper */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 16px",
          backgroundColor: "#fff",
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          marginBottom: "20px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        <ArrowLeftIcon
          onClick={() => navigate(-1)}
          style={{
            cursor: "pointer",
            padding: "4px",
            borderRadius: "4px",
            transition: "background-color 0.2s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#f5f5f5")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "transparent")
          }
        />

        <h2
          style={{
            margin: 0,
            fontSize: "18px",
            fontWeight: "600",
            color: "#333",
          }}
        >
          Group Loan Collection Report
        </h2>

        <ArrowRightIcon
          onClick={() => navigate(1)}
          style={{
            cursor: "pointer",
            padding: "4px",
            borderRadius: "4px",
            transition: "background-color 0.2s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#f5f5f5")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "transparent")
          }
        />
      </div>

      {/* FILTER */}
      <div className="glcr-filterBox">
        <div className="glcr-filter-wrapper">
          <div className="glcr-filter-item">
            <div className="glcr-filter">
              <label>Loan Type</label>
              <select
                value={loanTypeFilter}
                onChange={(e) => setLoanTypeFilter(e.target.value)}
              >
                <option value="">All Types</option>
                <option value="FIXED">Fixed Loan</option>
                <option value="INDIVIDUAL">Individual Loan</option>
              </select>
            </div>

            <div className="glcr-filter">
              <label>Select Center</label>
              <select
                value={selectedCenter}
                onChange={(e) => {
                  setSelectedCenter(e.target.value);
                  setSelectedGroup("");
                }}
              >
                <option value="">All Centers</option>
                {centers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.CenterName}
                  </option>
                ))}
              </select>
            </div>

            <div className="glcr-filter">
              <label>Select Group</label>
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                disabled={!selectedCenter}
              >
                <option value="">All Groups</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.groupname}, {g.groupcode}
                  </option>
                ))}
              </select>
            </div>
            <div className="glcr-filter">
              <label>From Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="glcr-filter">
              <label>To Date</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
          </div>

          <div className="glcr-filter-submit-btn">
            <button className="submit-btn" onClick={handleFilter}>
              Submit
            </button>
          </div>
        </div>
      </div>

      {/* EXPORT */}
      {filteredData.length > 0 && (
        <div className="glcr-pdf-download-btns">
          <button onClick={exportCSV}>
            <Download size={15} /> CSV
          </button>
          <button onClick={exportExcel}>
            <Download size={15} /> Excel
          </button>
          <button onClick={exportPDF}>
            <Download size={15} /> PDF
          </button>
        </div>
      )}

      {/* SEARCH */}
      <div className="glcr-report-filter">
        <input
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
        />
        <div>
          <label>Show </label>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              const value = e.target.value;
              setItemsPerPage(value === "All" ? "All" : Number(value));
              setCurrentPage(1);
            }}
          >
            {ITEMS_PER_PAGE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <span> entries</span>
        </div>
      </div>

      {/* TABLE */}
      <div className="glcr-report-table-container">
        <table className="glcr-report-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Loan Account No</th>
              <th>Loan Type</th>
              <th>Loan Amount</th>
              <th>Center Name</th>
              <th>Group Name</th>
              <th>Member Name</th>
              <th>Mobile No</th>
              <th>Emi No</th>
              <th>EMI Amount</th>
              <th>Paid Amount</th>
              <th>Outstanding</th>
              <th>Paid Date</th>
              <th>Status</th>
              <th>Reciept</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="14" align="center">
                  Loading...
                </td>
              </tr>
            ) : filteredData.length ? (
              filteredData.map((r, i) => {
                const nextemi =
                  i < filteredData.length - 1 ? filteredData[i + 1] : null;

                return (
                  <tr key={r.id}>
                    <td>
                      {itemsPerPage === "All"
                        ? i + 1
                        : (currentPage - 1) * itemsPerPage + i + 1}
                    </td>
                    <td>{r.loanAccount?.loanAccountNumber || "N/A"}</td>
                    <td>Group Loan {r.loanAccount?.loanType&&`(${r.loanAccount.loanType})`}</td>

                    <td>{r.loanAccount?.loanAmount}</td>
                    <td>{r.loanAccount?.centerName || "N/A"}</td>
                    <td>{r.loanAccount?.groupName || "N/A"}</td>
                    <td>{r.loanAccount?.memberName || "N/A"}</td>
                    <td>{r.member?.mobileNumber || "N/A"}</td>
                    <td>{r.installmentNo || "N/A"}</td>
                    <td>₹{`${r.emiAmount ? r.emiAmount : 0}`}</td>
                    <td>₹{`${r.paidAmount ? r.paidAmount : 0}`}</td>
                    <td>₹{`${r.outstanding ? r.outstanding : 0}`}</td>
                    <td>
                      {r.paidDate
                        ? new Date(r.paidDate).toLocaleDateString("en-GB")
                        : "-"}
                    </td>
                    <td className={r.status === "PAID" ? "status-paid" : ""}>
                      {r.status}
                    </td>

                    <td
                      className="receipt-cell"
                      onClick={() =>
                        navigate("/GroupLoan/GroupLoanCollectionReciept", {
                          state: { r, nextemi },
                        })
                      }
                    >
                      <FaFileInvoice className="receipt-icon" />
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="14" align="center">
                  No data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      {/* <div className="glcr-pagination">
        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>⬅ Prev</button>
        <span>Page {currentPage} of {totalPages}</span>
        <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next ➡</button>
      </div> */}
      {itemsPerPage !== "All" && (
        <div className="gledr-pagination">
          <button
            disabled={currentPage === 1 || filteredData.length === 0}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            ⬅ Prev
          </button>

          <span>
            {filteredData.length === 0
              ? "No data"
              : `Page ${currentPage} of ${totalPages}`}
          </span>

          <button
            disabled={currentPage === totalPages || filteredData.length === 0}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Next ➡
          </button>
        </div>
      )}
    </div>
  );
};

export default GroupLoanCollectionReport;
